import { DataTestId } from 'core/common/constants';
import { Image, MediaSourceVariant, Typography } from 'core/common/ui/ui-kit';
import { prepareHtml } from 'core/common/utils/prepareHtml';
import { SectionSpecialOfferAttributes } from 'core/flow/entities';
import { Offer } from 'core/offers/entities';
import { ButtonAnimation } from 'core/theme/ui/button';
import { notPaidUtmSources, paidUtmSources } from 'core/user/entities';
import {
  BodyHeaderPriceStyled,
  BodyHeaderStyled,
  ButtonStyled,
  CardBodyStyled,
  CardHeaderStyled,
  CardWrapperStyled,
  GoalInfoStyled,
  GoalWrapperStyled,
  GuaranteeBlockStyled,
  GuaranteeIconStyled,
  HorizontalLineStyled,
  SubscriptionActualPriceStyled,
  SubscriptionBlockStyled,
  SubscriptionOldPriceStyled,
  SubscriptionPricesBlockStyled,
  TotalBlockStyled,
  TrialDisclaimerStyled,
} from './styled';

export type SpecialOfferProps = {
  id?: string;
  isTimeUp: boolean;
  onGetReport: () => void;
  offer: Offer;
  timeUpOffer: Offer;
  content: SectionSpecialOfferAttributes['content'];
};

export const SpecialOffer = ({
  id,
  isTimeUp,
  onGetReport,
  offer,
  timeUpOffer,
  content,
}: SpecialOfferProps) => {
  const { paymentBlock, goalBlock, title } = content;
  const {
    header,
    paymentTitle,
    totalText,
    costPerMonthText,
    costPerMonthTextPaidTraffic,
    button,
    guaranteeText,
    trialDisclaimer,
    trialDisclaimerTimeUp,
  } = paymentBlock || {};

  const { goalTitle, description, image } = goalBlock || {};

  const offerZeroPrice = 0;
  const currentPrice = isTimeUp ? timeUpOffer.trialPriceWithCurrency : offer.trialPriceWithCurrency;

  if (!timeUpOffer || !offer) {
    return null;
  }

  return (
    <>
      {title && (
        <Typography
          gutterBottom
          align="center"
          variant="h1"
          dangerouslySetInnerHTML={prepareHtml(title)}
        />
      )}
      {goalBlock && (
        <GoalWrapperStyled>
          {image && <Image src={image.src} width={38} height={38} alt={image.alt} />}
          <GoalInfoStyled>
            <Typography
              variant="bodyExtraSmallRegular"
              dangerouslySetInnerHTML={prepareHtml(goalTitle)}
            />
            <Typography dangerouslySetInnerHTML={prepareHtml(description)} />
          </GoalInfoStyled>
        </GoalWrapperStyled>
      )}
      <CardWrapperStyled id={id}>
        <CardHeaderStyled>
          <Typography
            variant="bodyExtraSmallSemiBold"
            align="center"
            color="text.disabled"
            dangerouslySetInnerHTML={prepareHtml(header)}
          />
        </CardHeaderStyled>
        <CardBodyStyled>
          <BodyHeaderStyled>
            <Typography variant="h3" dangerouslySetInnerHTML={prepareHtml(paymentTitle)} />
            <BodyHeaderPriceStyled variant="h3">&nbsp;{currentPrice}</BodyHeaderPriceStyled>
          </BodyHeaderStyled>
          <TotalBlockStyled>
            <Typography
              variant="bodyMediumRegular"
              dangerouslySetInnerHTML={prepareHtml(totalText)}
            />
            <Typography variant="bodyMediumRegular">{currentPrice}</Typography>
          </TotalBlockStyled>
          <HorizontalLineStyled />
          <SubscriptionBlockStyled>
            <MediaSourceVariant name={paidUtmSources}>
              <Typography
                variant="bodyMediumRegular"
                dangerouslySetInnerHTML={prepareHtml(costPerMonthTextPaidTraffic)}
              />
              <SubscriptionPricesBlockStyled>
                <SubscriptionOldPriceStyled variant="bodySmallRegular">
                  {isTimeUp ? timeUpOffer.priceWithCurrency : offer.priceWithCurrency}
                </SubscriptionOldPriceStyled>
                <SubscriptionActualPriceStyled variant="bodyMediumRegular">
                  {offer.currencySymbol}
                  {offerZeroPrice}
                </SubscriptionActualPriceStyled>
              </SubscriptionPricesBlockStyled>
            </MediaSourceVariant>
            <MediaSourceVariant name={notPaidUtmSources}>
              <Typography
                variant="bodyMediumRegular"
                dangerouslySetInnerHTML={prepareHtml(costPerMonthText)}
              />
              <SubscriptionPricesBlockStyled>
                <SubscriptionOldPriceStyled variant="bodyExtraSmallRegular">
                  {!isTimeUp && timeUpOffer.priceWithCurrency}
                </SubscriptionOldPriceStyled>
                <Typography variant="bodyMediumRegular">
                  {isTimeUp ? timeUpOffer.priceWithCurrency : offer.priceWithCurrency}
                </Typography>
              </SubscriptionPricesBlockStyled>
            </MediaSourceVariant>
          </SubscriptionBlockStyled>
        </CardBodyStyled>
      </CardWrapperStyled>
      <GuaranteeBlockStyled>
        <GuaranteeIconStyled name="guarantee" width={24} height={24} />
        <Typography
          variant="bodySmallRegular"
          dangerouslySetInnerHTML={prepareHtml(guaranteeText)}
        />
      </GuaranteeBlockStyled>
      <ButtonStyled
        fullWidth
        variant="contained"
        animation={ButtonAnimation.SHINE}
        data-testid={DataTestId.GET_REPORT_BUTTON}
        onClick={onGetReport}
      >
        {button}
      </ButtonStyled>
      <TrialDisclaimerStyled
        variant="bodyExtraSmallRegular"
        dangerouslySetInnerHTML={prepareHtml(isTimeUp ? trialDisclaimerTimeUp : trialDisclaimer)}
      />
    </>
  );
};
